import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-xl border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        destructive:
          'border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40',
        outline:
          'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
      },
      status: {
        default:
          'bg-[#71717A]/10 text-zinc-700 dark:bg-[#A1A1AA]/15 dark:text-zinc-300',
        green:
          'bg-[#00A63E]/10 text-[#007A2E] dark:bg-[#00A63E]/15 dark:text-[#00C951]',
        amber:
          'bg-[#ECCD01]/15 text-[#8B4F00] dark:bg-[#FFA100]/12 dark:text-[#FFCA16]',
        blue: 'bg-[#0187F0]/10 text-[#005BB5] dark:bg-[#0070FD]/19 dark:text-[#6ABAFF]',
        red: 'bg-[#FB2C36]/10 text-[#C50009] dark:bg-[#FB2C36]/15 dark:text-[#FB2C36]',
        purple:
          'bg-[#AD46FF]/10 text-[#7B0DD0] dark:bg-[#AD46FF]/15 dark:text-[#AD46FF]',
      },
      type: {
        default: '',
        dot: '!pl-2 !pr-2.5 !py-[3px] !rounded-full !gap-1,5 leading-4',
      },
    },
    defaultVariants: {
      variant: 'default',
      status: 'default',
      type: 'default',
    },
  }
)

const dotVariants = cva('h-1.5 w-1.5 rounded-full', {
  variants: {
    status: {
      default: 'bg-zinc-700 dark:bg-zinc-300',
      green: 'bg-[#007A2E] dark:bg-[#00C951]',
      amber: 'bg-[#8B4F00] dark:bg-[#FFCA16]',
      blue: 'bg-[#005BB5] dark:bg-[#6ABAFF]',
      red: 'bg-[#C50009] dark:bg-[#FB2C36]',
      purple: 'bg-[#7B0DD0] dark:bg-[#AD46FF]',
    },
  },
  defaultVariants: {
    status: 'default',
  },
})

function Badge({
  className,
  variant,
  asChild = false,
  type,
  status,
  children,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean; type?: 'dot' }) {
  const Comp = asChild ? Slot : 'span'

  return (
    <Comp
      data-slot='badge'
      className={cn(badgeVariants({ variant, status, type }), className)}
      {...props}
    >
      {type === 'dot' && <span className={cn(dotVariants({ status }))} />}
      {children}
    </Comp>
  )
}

export { Badge, badgeVariants }
